/**
 * Image URL Utilities
 * Handles image URL construction with cache-busting, WebP/AVIF (Cloudinary), and responsive srcset.
 */

/**
 * Apply Cloudinary auto format/quality for WebP/AVIF when possible (Phase 6).
 * Inserts /f_auto,q_auto/ into Cloudinary URLs; returns others unchanged.
 */
function cloudinaryOptimizedUrl(url) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('cloudinary.com') || url.includes('/f_auto')) return url;
  // Insert transform after /upload/ and before version or path: upload/ -> upload/f_auto,q_auto/
  const uploadIdx = url.indexOf('/upload/');
  if (uploadIdx === -1) return url;
  const insertAt = uploadIdx + 8; // after '/upload/'
  return url.slice(0, insertAt) + 'f_auto,q_auto/' + url.slice(insertAt);
}

/**
 * Get image URL with cache-busting parameter
 * @param {string|object} image - Image string URL or object with secure_url/url
 * @param {string|Date} updatedAt - Entity's updatedAt timestamp for cache-busting
 * @param {string} fallback - Fallback image path (default: '/hero.png')
 * @param {boolean} webp - If true, use Cloudinary f_auto,q_auto for WebP/AVIF (default: true)
 * @returns {string} Image URL
 */
/** Legacy Cloudinary public_ids wrongly stored as image URL. These files don't exist; use fallback. */
const LEGACY_INVALID_URLS = /^(\.\.?\/)?subcategory-(round-neck|polo)(\.(jpg|jpeg|png|webp))?$/i;

export function getImageUrl(image, updatedAt = null, fallback = '/hero.png', webp = true) {
  if (!image) return fallback;
  
  let url;
  if (typeof image === 'string') {
    url = image;
  } else if (image?.secure_url) {
    url = image.secure_url;
  } else if (image?.url) {
    url = image.url;
  } else {
    return fallback;
  }

  if (url === fallback || url.endsWith(fallback)) {
    return url;
  }

  const barePath = url.replace(/^\//, '').split('?')[0];
  if (LEGACY_INVALID_URLS.test(barePath)) return fallback;

  if (webp) {
    url = cloudinaryOptimizedUrl(url);
  }

  if (updatedAt) {
    try {
      const timestamp = updatedAt instanceof Date 
        ? updatedAt.getTime() 
        : new Date(updatedAt).getTime();
      if (!isNaN(timestamp)) {
        const separator = url.includes('?') ? '&' : '?';
        return `${url}${separator}v=${timestamp}`;
      }
    } catch (e) {
      console.warn('[getImageUrl] Failed to parse updatedAt:', e);
    }
  }

  return url;
}

/**
 * Build responsive srcset for Cloudinary images (Phase 6).
 * @param {string} imageUrl - Base image URL (from getImageUrl)
 * @param {number[]} widths - Pixel widths, e.g. [320, 640, 960]
 * @returns {string} srcset value or empty string if not Cloudinary
 */
export function getImageSrcSet(imageUrl, widths = [320, 640, 960, 1280]) {
  if (!imageUrl || !imageUrl.includes('cloudinary.com')) return '';
  const uploadIdx = imageUrl.indexOf('/upload/');
  if (uploadIdx === -1) return '';
  const prefix = imageUrl.slice(0, uploadIdx + 8); // through '/upload/'
  let rest = imageUrl.slice(uploadIdx + 8);
  if (rest.startsWith('f_auto,q_auto/')) rest = rest.slice(13);
  const entries = widths.map(w => `${prefix}w_${w},c_scale,f_auto,q_auto/${rest} ${w}w`).join(', ');
  return entries;
}

/**
 * Get image URL for product images (handles array of images)
 * @param {string|object|array} images - Single image or array of images
 * @param {string|Date} updatedAt - Entity's updatedAt timestamp
 * @param {string} fallback - Fallback image path
 * @returns {string|array} Image URL(s) with cache-busting
 */
export function getProductImageUrl(images, updatedAt = null, fallback = '/hero.png') {
  if (Array.isArray(images)) {
    return images.map(img => getImageUrl(img, updatedAt, fallback));
  }
  return getImageUrl(images, updatedAt, fallback);
}
